import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div data-testid="forgot-page" className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="50 States Sober" className="w-16 h-16 rounded-full object-cover ring-1 ring-[var(--line)]" />
        </div>

        {sent ? (
          <div data-testid="forgot-sent" className="text-center">
            <Mail className="w-10 h-10 mx-auto text-[var(--forest)] mb-4" strokeWidth={1.25} />
            <h1 className="font-serif-display text-3xl mb-3">Check your email</h1>
            <p className="text-[var(--ink-soft)] leading-relaxed mb-8">
              If an account with <strong className="text-[var(--ink)]">{email}</strong> exists, we&apos;ve sent a link to reset the password.
              The link works once and expires in 1 hour.
            </p>
            <p className="text-xs text-[var(--ink-soft)] mb-8">
              Didn&apos;t get it? Check your spam folder, or try again in a few minutes.
            </p>
            <Link to="/login" data-testid="forgot-back-to-login"
              className="inline-flex items-center gap-2 text-sm text-[var(--forest)] hover:underline">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl text-center mb-2">Forgot password?</h1>
            <p className="text-[var(--ink-soft)] text-center mb-10">
              Enter your email and we&apos;ll send you a link to set a new one.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid="forgot-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full px-4 py-3 border border-[var(--line)] rounded-sm text-sm focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]"
              />
              {err && <div data-testid="forgot-error" className="text-sm text-red-600">{err}</div>}
              <button data-testid="forgot-submit" disabled={loading || !email}
                className="w-full bg-[var(--terracotta)] text-[var(--ink)] py-3 rounded-sm text-sm hover:bg-[var(--terracotta-dark)] disabled:opacity-50">
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>

            <p className="text-sm text-center text-[var(--ink-soft)] mt-8">
              Remembered it? <Link to="/login" data-testid="forgot-to-login" className="text-[var(--forest)] underline">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
